import React from "react";

const RELOAD_FLAG = "chunkReloadAttempted";

/**
 * Recovers from a failed lazy-chunk load.
 *
 * Once routes are code-split, a tab left open across a deploy holds an index.html that
 * references the OLD hashed chunks. Navigating then requests a file that may no longer
 * exist and React throws a ChunkLoadError, which unmounts the tree to a blank page.
 * Reloading fetches the current index.html and fixes it.
 *
 * The sessionStorage flag matters: without it, a chunk that is genuinely broken (rather
 * than merely stale) reload-loops forever. One attempt, then show something a person can
 * act on. The flag is cleared on any successful render, so a later stale-deploy reload
 * still works within the same tab.
 *
 * The deploy no longer passes `--delete` to `s3 sync`, which makes this rare — belt and
 * braces, since the failure mode is a white screen.
 */
class ChunkErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { failed: false };
  }

  static getDerivedStateFromError(error) {
    const message = String(error?.message || error || "");
    const isChunkError =
      error?.name === "ChunkLoadError" ||
      /Loading chunk \S+ failed/i.test(message) ||
      /Failed to fetch dynamically imported module/i.test(message);

    if (isChunkError && !sessionStorage.getItem(RELOAD_FLAG)) {
      sessionStorage.setItem(RELOAD_FLAG, "1");
      window.location.reload();
      // Render nothing while the reload is in flight.
      return { failed: false };
    }
    return { failed: true };
  }

  componentDidMount() {
    sessionStorage.removeItem(RELOAD_FLAG);
  }

  componentDidCatch(error, info) {
    console.error("Route chunk failed to load:", error, info);
  }

  render() {
    if (this.state.failed) {
      return (
        <div className="flex min-h-[50vh] flex-col items-center justify-center gap-3 text-center">
          <p className="text-sm text-slate-700">This page could not be loaded.</p>
          <button
            type="button"
            onClick={() => {
              sessionStorage.removeItem(RELOAD_FLAG);
              window.location.reload();
            }}
            className="rounded-lg bg-gradient-to-br from-flamingo-500 to-flamingo-600 px-4 py-2 text-sm font-semibold text-white"
          >
            Reload
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

export default ChunkErrorBoundary;
